import apiClient from './apiClient'
import { LoginCredentials, RegisterData, AuthResponse } from '../types/user.types'

const AUTH_TOKEN_STORAGE_KEY = 'authToken'

// Cache para evitar múltiplas tentativas de login simultâneas
let loginPromise: Promise<{ data: AuthResponse }> | null = null
let lastLoginAttempt = 0
const LOGIN_DEBOUNCE_MS = 2000 // 2 segundos entre tentativas

export const authService = {
  async login(credentials: LoginCredentials): Promise<{ data: AuthResponse }> {
    const now = Date.now()
    
    // Debounce: evitar tentativas muito próximas
    if (now - lastLoginAttempt < LOGIN_DEBOUNCE_MS) {
      throw new Error('Aguarde antes de tentar novamente')
    }
    
    lastLoginAttempt = now
    
    // Se já há uma tentativa em andamento, aguardar ela
    if (loginPromise) {
      return loginPromise
    }
    
    // Criar nova promise de login
    loginPromise = this.performLogin(credentials)
    
    try {
      const result = await loginPromise
      return result
    } finally {
      // Limpar promise após completar
      loginPromise = null
    }
  },

  async performLogin(credentials: LoginCredentials): Promise<{ data: AuthResponse }> {
    const response = await apiClient.post('/auth/login', credentials)
    const { user, token } = response.data.data
    
    // Salvar usuário e token para fallback de autenticação via Authorization
    localStorage.setItem('user', JSON.stringify(user))
    if (token) {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token)
    }
    
    return response.data
  },

  async register(userData: RegisterData): Promise<{ data: AuthResponse }> {
    const response = await apiClient.post('/auth/register', userData)
    const { user, token } = response.data.data
    
    // Salvar usuário e token para fallback de autenticação via Authorization
    localStorage.setItem('user', JSON.stringify(user))
    if (token) {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token)
    }
    
    return response.data
  },

  async verifyToken(): Promise<{ data: any }> {
    const response = await apiClient.get('/auth/verify')
    return response.data
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout')
    // Cookie HttpOnly será limpo pelo backend e fallback local também
    localStorage.removeItem('user')
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
  },

  getStoredUser() {
    const user = localStorage.getItem('user')
    return user ? JSON.parse(user) : null
  },

  getStoredToken() {
    return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
  },

  isAuthenticated() {
    // Aceita usuário salvo + token (fallback)
    const user = this.getStoredUser()
    const token = this.getStoredToken()
    return !!user || !!token
  }
}

